import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaNetworkWired } from '@react-icons/all-files/fa/FaNetworkWired';
import { FaProjectDiagram } from '@react-icons/all-files/fa/FaProjectDiagram';
import { FaCog } from '@react-icons/all-files/fa/FaCog';
import { FaHome } from '@react-icons/all-files/fa/FaHome';
import { FaPlus } from '@react-icons/all-files/fa/FaPlus';
import Breadcrumb from '../../../../commons/Breadcrumb';
import PageHeader from '../../../../commons/PageHeader';
import PageAlert, { usePageAlert } from '../../../../commons/PageAlert';
import Confirm, { useConfirm } from '../../../../commons/Confirm';
import { EditButton, DeleteButton, DesignButton, RenderActions, WorkflowEmpresaActividadButton } from '../../../../commons/DataGridActions';
import ProfaseDataGrid from '../../../../commons/Datagrid';
import { useAppDispatch, useAppSelector } from '../../../../store';
import { getEntities, deleteEntity, reset } from '../../../../manager/workflow-empresa.reducer';
import { FaCheck } from '@react-icons/all-files/fa/FaCheck';
import { FaTimes } from '@react-icons/all-files/fa/FaTimes';

const BASE = '/configuracion/workflow/workflow-empresa';

const objCell = (params: any) => {

  console.log('objCell', params);
  if (params.field === 'disenoPersonalizado') {
    return params.value ? <FaCheck /> : <FaTimes />;
  }

  if (params.field === 'activo') {
    return params.value ? <FaCheck /> : <FaTimes />;
  }

  if (params.field === 'workflowClase') {
    return params.value?.nombre ?? `-- Sin Clase --`;
  }

  if (params.field === 'empresa.grupoEmpresarial.alias') {
    return params.row.empresa?.grupoEmpresarial?.alias
      ?? (params.row.empresa?.grupoEmpresarial?.grpAliasDecomsa ?? (params.row.empresa?.grupoEmpresarial?.grpAliasEquilibrio ?? ''));
  }


  if (params.field === 'grupoEmpresarial') {
    return params.row.empresa?.grupoEmpresarial?.alias
      ?? (params.row.empresa?.grupoEmpresarial?.grpAliasDecomsa ?? (params.row.empresa?.grupoEmpresarial?.grpAliasEquilibrio ?? ''));
  }

  if (params.field === 'empresa') {
    return `${params.value?.rfc ?? ''} - ${params.value?.razonSocial ?? ''}`;
  }

  return params.value?.nombre ?? `#${params.value?.nombre}`;
};

export default function WorkflowEmpresaPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const topRef = useRef<HTMLDivElement>(null);

  const entities = useAppSelector(s => s.workflowEmpresa.entities);
  const totalItems = useAppSelector(s => s.workflowEmpresa.totalItems);
  const loading = useAppSelector(s => s.workflowEmpresa.loading);
  const updating = useAppSelector(s => s.workflowEmpresa.updating);
  const updateSuccess = useAppSelector(s => s.workflowEmpresa.updateSuccess);

  const [rows, setRows] = useState<any[]>([]);
  const [lastParams, setLastParams] = useState<any>(null);

  const { alert, showToast } = usePageAlert();
  const { confirmConfig, ask, dismiss, setConfirmLoading } = useConfirm();

  useEffect(() => {
    const msg = (location.state as any)?.successMsg;
    if (msg) { showToast('success', msg); window.history.replaceState({}, ''); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (updateSuccess) {
      dispatch(reset());
      dismiss();
      if (lastParams) dispatch(getEntities(lastParams));
      showToast('success', 'Registro eliminado correctamente.');
    }
  }, [updateSuccess]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setConfirmLoading(updating); }, [updating]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setRows(entities ?? []); }, [entities]);

  const fetchData = (params: any) => {
    setLastParams(params);
    dispatch(getEntities(params));
  };

  const renderActions = (params: any) => (
    <RenderActions>
      <DesignButton onClick={() => navigate(`${BASE}/${params.row.id}/diseno`)} title="Diseño" />
      <WorkflowEmpresaActividadButton onClick={() => navigate(`/configuracion/workflow/workflow-empresa-actividad?workflowNodo.tipoNodo=actividad&workflowEmpresa.id=${params.row.id}`)} title="Actividades"></WorkflowEmpresaActividadButton>
      <EditButton onClick={() => navigate(`${BASE}/${params.row.id}/edit`)} />
      <DeleteButton onClick={() => {
        const label = params.row.empresa?.nombre ?? `#${params.row.id}`;
        ask(
          <>¿Confirmas que deseas eliminar el registro <strong>{label}</strong>? Esta acción no se puede deshacer.</>,
          () => dispatch(deleteEntity(params.row.id)),
          { title: 'Eliminar Workflow Empresa', confirmLabel: 'Eliminar', variant: 'danger' },
        );
        topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }} />
    </RenderActions>
  );

  const cols = [
    { field: 'id', headerName: 'ID', flex: 0.4 },
    { field: 'empresa.grupoEmpresarial.alias', headerName: 'Grupo', flex: 1.5, renderCell: objCell },
    { field: 'empresa', headerName: 'RFC', flex: 1.8, renderCell: objCell },
    { field: 'empresa', headerName: 'Razón Social', flex: 1.8, renderCell: objCell },
    { field: 'workflow', headerName: 'Workflow', flex: 1.8, renderCell: objCell },
    { field: 'workflowClase', headerName: 'Clase Workflow', flex: 1.5, renderCell: objCell },
    { field: 'activo', headerName: 'Activo', flex: 1.5, renderCell: objCell },
    { field: 'disenoPersonalizado', headerName: 'Diseño Pers.', flex: 1.5, renderCell: objCell },
    { field: '__actions', headerName: '', flex: 0.8, renderCell: renderActions },
  ];

  const filters = [
    { label: 'ID', name: 'id' },
    { label: 'Grupo', name: 'empresa.grupoEmpresarial.alias' },
    { label: 'Empresa', name: 'empresa.razonSocial' },
    { label: 'Workflow', name: 'profaseWorkflowNombre' },
    { label: 'Clase Workflow', name: 'workflowClaseNombre' },
  ];

  return (
    <div style={g.page} ref={topRef}>
      <Breadcrumb items={[
        { label: 'Inicio', to: '/', icon: <FaHome /> },
        { label: 'Configuración', to: '/configuracion', icon: <FaCog /> },
        { label: 'Workflow', to: '/configuracion/workflow', icon: <FaProjectDiagram /> },
        { label: 'Workflow Empresa', icon: <FaNetworkWired /> },
      ]} />

      <PageHeader
        icon={<FaNetworkWired />}
        iconColor="#14b8a6"
        title="Workflow Empresa"
        subtitle="Asignación de workflows a empresas"
        action={{
          label: <><FaPlus style={{ marginRight: 6 }} />Nuevo Registro</>,
          onClick: () => navigate(`${BASE}/new`),
          color: '#14b8a6',
        }}
      />

      <Confirm config={confirmConfig} />
      {alert && <PageAlert alert={alert} />}

      <ProfaseDataGrid
        cols={cols}
        title=""
        filters={filters}
        fetch={fetchData}
        rowCount={totalItems}
        loading={loading}
        rows={rows}
      />
    </div>
  );
}

const g: Record<string, React.CSSProperties> = {
  page: { display: 'flex', flexDirection: 'column', gap: 24 },
};
